import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Input } from '@enechain/ecloud-designsystem'

// A debounced text input that also follows external `value` changes.
// The design system's DebounceTextInput cannot be used here: it initializes
// its local state from `value` once and never syncs afterwards, so an
// external reset (e.g. a filter-clear button or browser back navigation)
// would leave stale text in the input.
type Props = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  debounceMs?: number
}

const DebounceTextInput: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  disabled,
  debounceMs = 300,
}) => {
  const [localValue, setLocalValue] = useState(value)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setLocalValue(value)
    // Cancel any pending debounced onChange when the value is changed from
    // the outside (e.g. a filter-clear button). Otherwise the stale timer
    // fires afterwards and silently overwrites the external change.
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [value])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setLocalValue(newValue)

      if (timerRef.current != null) {
        clearTimeout(timerRef.current)
      }

      timerRef.current = setTimeout(() => {
        onChange(newValue)
      }, debounceMs)
    },
    [onChange, debounceMs],
  )

  useEffect(() => {
    return () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return (
    <Input
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
    />
  )
}

export default React.memo(DebounceTextInput)
